import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { auth, functions, storage } from "@/lib/firebase";

export type ScryfallishLite = {
  id?: string | null;
  name?: string | null;
  image_uris?: { normal?: string | null; large?: string | null } | null;
  card_faces?: { image_uris?: { normal?: string | null; large?: string | null } | null }[] | null;
};

export const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

export function computeDefaultArt(card: ScryfallishLite | null | undefined): string | null {
  if (!card) return null;
  return (
    card.card_faces?.[0]?.image_uris?.normal ??
    card.image_uris?.normal ??
    card.card_faces?.[0]?.image_uris?.large ??
    card.image_uris?.large ??
    null
  );
}

function extForType(t: string): string {
  if (t === "image/png") return ".png";
  if (t === "image/webp") return ".webp";
  return ".jpg";
}

export async function fileToBase64Resized(f: File, maxSide = 768, quality = 0.85): Promise<string> {
  const img = document.createElement("img");
  const url = URL.createObjectURL(f);
  try {
    await new Promise<void>((res, rej) => {
      img.onload = () => res();
      img.onerror = () => rej(new Error("Failed to load image"));
      img.src = url;
    });
    const { width, height } = img;
    const scale = Math.min(1, maxSide / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");
    ctx.drawImage(img, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    return (dataUrl.split(",")[1] || "");
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function precheckImage(file: File, card?: { id?: string | null; name?: string | null }) {
  const imgB64 = await fileToBase64Resized(file);
  const call = httpsCallable(functions, "moderateImage");
  const resp = (await call({
    imageBase64: imgB64,
    thresholds: { adult: "POSSIBLE", violence: "POSSIBLE", racy: "LIKELY" },
    context: { where: "userCardImage", scryfall_id: card?.id, name: card?.name },
  })) as any;
  const { allowed, reasons } = resp?.data || {};
  if (!allowed) {
    const reasonText = Array.isArray(reasons) && reasons.length ? reasons.join(", ") : "Inappropriate content";
    throw new Error(`Image blocked by content protection (${reasonText}).`);
  }
}

export async function uploadUserCardImage(
  uid: string,
  file: File,
  card: { id?: string | null; name?: string | null },
  onProgress?: (pct: number) => void,
): Promise<{ url: string; path: string }> {
  if (!ACCEPTED_TYPES.includes(file.type))
    throw new Error("Unsupported image type. Use PNG, JPG/JPEG, or WEBP.");
  if (file.size > 10 * 1024 * 1024)
    throw new Error("Image is too large. Max 10MB.");

  // Pre-moderate
  await precheckImage(file, card);

  const filename = `${card.id ?? "card"}_${Date.now()}${extForType(file.type)}`.replace(/[^a-zA-Z0-9._-]/g, "_");
  const objectPath = `users/${uid}/cardImages/${filename}`;
  const storageRef = ref(storage, objectPath);

  const task = uploadBytesResumable(storageRef, file, {
    contentType: file.type,
    customMetadata: {
      scryfall_id: String(card.id ?? ""),
      owner_uid: String(uid),
      card_name: String(card.name ?? ""),
      moderation: "approved",
    },
  });

  const url: string = await new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snap) => onProgress?.(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      (err) => reject(err),
      async () => resolve(await getDownloadURL(task.snapshot.ref)),
    );
  });

  return { url, path: objectPath };
}

/**
 * Given current local state (file, uploadedUrl), return final image URL and custom path/URL.
 * Falls back to Scryfall art when nothing uploaded.
 */
export async function prepareImageForSave(opts: {
  uid: string;
  card: ScryfallishLite;
  file: File | null;
  uploadedUrl: string | null;
  onProgress?: (pct: number) => void;
}): Promise<{ image: string | null; custom_image_url: string | null; custom_image_path: string | null }> {
  const defaultArt = computeDefaultArt(opts.card);
  let custom: { url: string; path: string } | null = null;
  let finalImage: string | null = opts.uploadedUrl ?? null;

  if (!finalImage && opts.file) {
    custom = await uploadUserCardImage(opts.uid, opts.file, { id: opts.card.id, name: opts.card.name as any }, opts.onProgress);
    finalImage = custom.url;
  }

  return {
    image: finalImage ?? defaultArt ?? null,
    custom_image_url: custom?.url ?? null,
    custom_image_path: custom?.path ?? null,
  };
}
